import { Copy, Printer, Trash2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api, imgUrl, errMsg } from "@/lib/api";
import { euro, fmtDate, countryName, shippingText } from "@/lib/shop";
import { printOrder } from "@/lib/printLabel";
import { FulfillmentActions } from "./FulfillmentActions";

const STATUS = { preparato: "Preparato", spedito: "Spedito", annullato: "Annullato" };

const CancelOrder = ({ o, onUpdated }) => {
  const cancel = async () => {
    try {
      const { data } = await api.post(`/admin/orders/${o.id}/cancel`);
      toast.success("Ordine annullato: quantità tornate in vetrina");
      onUpdated(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button data-testid={`cancel-order-button-${o.id}`} className="btn-ghost-mc">
          <Undo2 className="h-4 w-4" /> Annulla ordine
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Annullare l'ordine {o.number}?</AlertDialogTitle>
          <AlertDialogDescription>
            Le quantità degli articoli tornano in vetrina. L'eventuale rimborso al cliente va fatto dalla dashboard Stripe.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`cancel-order-dismiss-${o.id}`}>Indietro</AlertDialogCancel>
          <AlertDialogAction data-testid={`confirm-cancel-order-${o.id}`} onClick={cancel}>Annulla ordine</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const copy = async (o) => {
  try {
    await navigator.clipboard.writeText(shippingText(o));
    toast.success("Dati spedizione copiati");
  } catch {
    toast.error("Copia non riuscita");
  }
};

const DeleteOrder = ({ o, onDeleted }) => {
  const del = async () => {
    try {
      await api.delete(`/admin/orders/${o.id}`);
      toast.success("Ordine eliminato");
      onDeleted(o.id);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button data-testid={`delete-order-button-${o.id}`} className="btn-ghost-mc sm:ml-auto hover:!bg-red-50 hover:text-red-700">
          <Trash2 className="h-4 w-4" /> Elimina
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Eliminare l'ordine {o.number}?</AlertDialogTitle>
          <AlertDialogDescription>L'ordine sparisce dalla lista. Le quantità già scalate non tornano in vendita.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`cancel-delete-order-${o.id}`}>Annulla</AlertDialogCancel>
          <AlertDialogAction data-testid={`confirm-delete-order-${o.id}`} onClick={del}>Elimina</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export const OrderCard = ({ o, admin = false, onDeleted, onUpdated }) => {
  const a = o.address;
  return (
    <div data-testid={`order-card-${o.id}`} className="card-mc rise overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-[#fffbe6] px-4 py-3">
        <div>
          <div data-testid={`order-number-${o.id}`} className="font-display text-base font-extrabold">{o.number}</div>
          <div className="text-xs text-muted-foreground">{fmtDate(o.paid_at || o.created_at)}</div>
        </div>
        <span data-testid={`order-status-${o.id}`} className="rounded-full border-2 border-[#0b0b0f] bg-[#FFD60A] px-3 py-0.5 text-xs font-black uppercase">{STATUS[o.fulfillment] || "Pagato"}</span>
      </div>
      {o.tracking_code && (
        <div data-testid={`order-tracking-${o.id}`} className="border-b border-border bg-white px-4 py-2 text-sm">
          Codice di tracciamento: <b className="break-all">{o.tracking_code}</b>
        </div>
      )}
      <div className="grid gap-4 p-4 sm:grid-cols-2">
        <div className="space-y-2">
          {o.items.map((i) => (
            <div key={i.product_id} className="flex items-center gap-3">
              <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg border border-border bg-white">
                {i.image && <img src={imgUrl(i.image)} alt="" className="h-full w-full object-contain" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold">{i.name}</div>
                {i.condition_label && <div data-testid="order-item-condition" className="text-xs font-semibold text-violet-700">{i.condition_label}</div>}
                <div className="text-xs text-muted-foreground">Quantità: {i.quantity} × {euro(i.price)}</div>
              </div>
            </div>
          ))}
          <div className="border-t border-dashed border-border pt-2 text-sm">
            <div className="flex justify-between"><span>Spedizione: {o.shipping.name}</span><span>{euro(o.shipping.cost)}</span></div>
            <div className="flex justify-between font-display text-base font-extrabold">
              <span>Totale</span><span data-testid={`order-total-${o.id}`}>{euro(o.total)}</span>
            </div>
          </div>
        </div>
        <div className="rounded-xl bg-muted/60 p-3 text-sm leading-relaxed" data-testid={`order-address-${o.id}`}>
          <div className="font-bold">{a.full_name}</div>
          <div>{a.address}</div>
          <div>{a.zip} {a.city}{a.province ? ` (${a.province})` : ""}</div>
          <div>{countryName(a.country)}</div>
          <div className="mt-1 text-muted-foreground">Tel: {a.phone}</div>
          <div className="text-muted-foreground break-all">{o.email}</div>
        </div>
      </div>
      {admin && (
        <div className="flex flex-wrap gap-2 border-t border-border px-4 py-3">
          <button data-testid={`copy-shipping-data-button-${o.id}`} className="btn-ghost-mc" onClick={() => copy(o)}>
            <Copy className="h-4 w-4" /> Copia dati spedizione
          </button>
          <button
            data-testid={`print-order-label-button-${o.id}`}
            className="btn-mc"
            onClick={() => { if (!printOrder(o)) toast.error("Consenti i popup per stampare"); }}
          >
            <Printer className="h-4 w-4" /> Stampa
          </button>
          {o.fulfillment !== "annullato" && <FulfillmentActions o={o} onUpdated={onUpdated} />}
          {o.fulfillment !== "annullato" && <CancelOrder o={o} onUpdated={onUpdated} />}
          <DeleteOrder o={o} onDeleted={onDeleted} />
        </div>
      )}
    </div>
  );
};
